import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { LanguageSwitcher } from '../../components/common';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ThemeColors } from '../../styles/ThemeStyles';

const LogoContainer = styled.div`
  margin-bottom: 10px;
`;

const LogoImage = styled.img`
  width: 300px;
  height: auto;
`;

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #f0f5ff;
  padding: 20px;
  position: relative;
`;

const LoginForm = styled.form`
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 450px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const Title = styled.h2`
  margin-top: 20px;
  margin-bottom: 30px;
  color: ${ThemeColors.primary.main};
  text-align: center;
  font-size: 28px;
  font-weight: 600;
`;

const LanguageSwitcherContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 30px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  margin-bottom: 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-sizing: border-box;
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px rgba(26, 66, 138, 0.2);
  }
`;

const PasswordContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 15px;
`;

const PasswordToggleButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 15px;
`;

const Checkbox = styled.input`
  margin-right: 8px;
  cursor: pointer;
  accent-color: ${ThemeColors.primary.main};
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  color: #2D2123;
  cursor: pointer;
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  background-color: ${ThemeColors.primary.main};
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${ThemeColors.primary.dark};
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.p`
  color: #F44336;
  margin-top: 10px;
  text-align: center;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

export const LoginScreenDesktop = () => {
  console.log("LoginScreenDesktop component rendering");
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [localError, setLocalError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { signIn, loading, error, user, userRole } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Get redirect from URL params first, then fall back to sessionStorage
  const redirectTo = searchParams.get('redirect') || sessionStorage.getItem('loginRedirect');

  // Clear local error when usernameOrEmail/password changes
  useEffect(() => {
    if (localError) {
      setLocalError('');
    }
  }, [usernameOrEmail, password]);

  // Store redirect in sessionStorage if present in URL params
  useEffect(() => {
    if (redirectTo && redirectTo.startsWith('/')) {
      sessionStorage.setItem('loginRedirect', redirectTo);
    }
  }, [redirectTo]);

  // Redirect after successful login - let ManagerNavigator handle the actual redirect
  // This just ensures we navigate away from login screen
  useEffect(() => {
    if (user && userRole) {
      console.log("User logged in, RootNavigator will switch navigators");
      // The RootNavigator will switch to ManagerNavigator/SalesRepNavigator
      // which will handle the redirect via LoginRedirectHandler
    }
  }, [user, userRole]);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setLocalError('');
    setIsSubmitting(true);
    
    try {
      // Use the enhanced signIn method that supports username or email
      await signIn(usernameOrEmail, password, rememberMe);
    } catch (e: any) {
      // Error handling - errors are already handled in AuthContext
      console.error('Login error:', e);
      // The error will be shown via the error state from useAuth
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Determine which error to show (local has priority)
  const displayError = localError || error;

  return (
    <LoginContainer>
      <LoginForm onSubmit={handleLogin}>
        <LanguageSwitcherContainer>
          <LanguageSwitcher />
        </LanguageSwitcherContainer>
        <LogoContainer>
          <LogoImage src="/assets/erp-logo.svg" alt="ERP Mock Logo" />
        </LogoContainer>
        <Title>{t('auth.portalLogin')}</Title>
        <Input
          type="text"
          placeholder={t('auth.usernameOrEmail', 'Username or Email')}
          value={usernameOrEmail}
          onChange={(e) => setUsernameOrEmail(e.target.value)}
          required
        />
        <PasswordContainer>
          <Input
            type={showPassword ? "text" : "password"}
            placeholder={t('auth.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <PasswordToggleButton 
            type="button" 
            onClick={togglePasswordVisibility}
            aria-label={showPassword ? t('auth.hidePassword', 'Hide password') : t('auth.showPassword', 'Show password')}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </PasswordToggleButton>
        </PasswordContainer>
        <CheckboxContainer>
          <Checkbox 
            type="checkbox" 
            id="rememberMe" 
            checked={rememberMe} 
            onChange={(e) => setRememberMe(e.target.checked)} 
          />
          <CheckboxLabel htmlFor="rememberMe">
            {t('auth.rememberMe', 'Remember me')}
          </CheckboxLabel>
        </CheckboxContainer>
        <Button type="submit" disabled={isSubmitting || loading}>
          {isSubmitting || loading ? t('auth.loggingIn') : t('auth.login')}
        </Button>
        {displayError && (
          <ErrorText>
            <AlertCircle size={16} />
            {displayError}
          </ErrorText>
        )}
      </LoginForm>
    </LoginContainer>
  );
};